import {
    apiErrorMessage,
    filterControlsOf,
    ListPager,
    ListToolbar,
    oneSpec,
    type Prefetch,
} from '@bemor/web-kit';
import {
    HttpLogDetailDrawer,
    HttpLogTable,
    PageHeader,
} from '@bemor/web-kit/ui';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type { HttpLog, HttpLogFacets, HttpLogSummary } from '@/api/types';
import { adminListPrefetch, filterDefs, useAdminList } from '@/lib/lists';
import { useOne } from '@/lib/queries';

const PATH = '/staff/http-logs';
const PAGE_KEY = 'http-logs';

const httpLogFilters = filterDefs(PATH, {
    client: 'Client',
    host: 'Host',
    level: 'Level',
    status: 'Status',
    direction: 'Direction',
    merchant_id: 'Merchant',
    correlation_id: 'Correlation id',
    range: 'When',
});

/** A status class or, typed in the URL, an exact code (`404`): the API takes both. */
const STATUS_OPTIONS = [
    { value: '2xx', label: '2xx success' },
    { value: '3xx', label: '3xx redirect' },
    { value: '4xx', label: '4xx client error' },
    { value: '5xx', label: '5xx server error' },
];

/** The extra reads of the page: the filter choices, and the log in the drawer (BM-46 §2.0). */
export const httpLogsReads = (id?: string) => ({
    facets: oneSpec('/staff/http-logs/facets'),
    detail: id ? oneSpec(`/staff/http-logs/${id}`) : null,
});

const listPrefetch = adminListPrefetch(PAGE_KEY, PATH);

/** The route's prefetch: the views, then the rows of the view; the facets; the open log. */
export const httpLogsPrefetch: Prefetch = (context) => {
    const r = httpLogsReads(context.params.id);
    return [
        ...listPrefetch(context),
        r.facets,
        ...(r.detail ? [r.detail] : []),
    ];
};

/**
 * `/http-logs` and `/http-logs/:id` (BM-43 §2.1): every call api made to an outside service, newest first (inbound
 * webhooks live in another table and are not shown here). The list never carries bodies; the drawer loads the full row (secrets already masked).
 */
export function HttpLogsPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [params] = useSearchParams();
    const { state, query } = useAdminList<HttpLogSummary>(PAGE_KEY, PATH, {
        // The kit's HTTP log table draws its own columns.
        columns: [],
        filters: httpLogFilters,
    });
    const reads = httpLogsReads(id);
    // The filter choices within my scope (BM-43): the clients, hosts and levels that have logs.
    const facetsQuery = useOne<HttpLogFacets>(reads.facets.path);
    const facets = facetsQuery.data?.data;
    const detail = useOne<HttpLog>(reads.detail?.path ?? null);
    const search = params.toString() ? `?${params.toString()}` : '';
    return (
        <div>
            <PageHeader
                title="HTTP logs"
                meta="Calls Bemor made to outside services (NMI, Anrok, Corpay, Google, email), newest first. Secrets are masked when the log is written."
            />
            <ListToolbar
                className="mb-3"
                state={state}
                exportable={false}
                controls={filterControlsOf(state, {
                    client: { options: facets?.clients ?? [] },
                    host: { options: facets?.hosts ?? [] },
                    level: { options: facets?.levels ?? [] },
                    status: { options: STATUS_OPTIONS },
                    merchant_id: { kind: 'text', label: 'Merchant id' },
                    correlation_id: { kind: 'text', label: 'Correlation id' },
                    range: { label: 'When' },
                })}
            />
            {query.error ? (
                <div
                    role="alert"
                    className="text-destructive rounded-md border p-4 text-sm"
                    data-testid="list-error"
                >
                    {apiErrorMessage(query.error)}
                </div>
            ) : (
                <>
                    <HttpLogTable
                        rows={query.rows}
                        loading={query.loading}
                        onRow={(row) =>
                            navigate(`/http-logs/${row.id}${search}`)
                        }
                        empty={
                            <p className="text-muted-foreground p-6 text-center text-sm">
                                No calls match these filters.
                            </p>
                        }
                    />
                    <ListPager
                        className="mt-3"
                        state={state}
                        rows={query.rows.length}
                        total={query.total}
                        capped={query.capped}
                        disabled={query.fetching}
                    />
                </>
            )}
            <HttpLogDetailDrawer
                open={Boolean(id)}
                onOpenChange={(o) => {
                    if (!o) navigate(`/http-logs${search}`);
                }}
                log={detail.data?.data ?? null}
                loading={detail.isPending && Boolean(id)}
                error={detail.isError ? apiErrorMessage(detail.error) : null}
            />
        </div>
    );
}
